import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { api } from '../../services/api';
import { MaintenanceReportWorkTimeline } from '../../components/maintenance/MaintenanceReportWorkTimeline';
import { formatPhoneDisplay } from '../../utils/solicitudFormDisplay';
import { formatDate, formatDateLong, formatDateTime, formatMonthYearLong } from '../../utils/date';

const TABS = [
  { id: 'account' as const, label: 'Mi cuenta' },
  { id: 'payments' as const, label: 'Pagos' },
  { id: 'reports' as const, label: 'Reportes' },
  { id: 'renewal' as const, label: 'Renovación' },
];

const mxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

function formatMoney(n: number) {
  return mxn.format(Number.isFinite(n) ? n : 0);
}

function getContractBadge(tenant: any): { label: string; className: string } {
  const st = tenant?.status;
  if (st === 'cancelado' || st === 'terminado') {
    return { label: 'Inactivo', className: 'bg-gray-100 text-gray-700' };
  }
  const end = tenant?.endDate ? new Date(tenant.endDate) : null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  if (end) {
    const endDay = new Date(end);
    endDay.setHours(0, 0, 0, 0);
    const diffDays = (endDay.getTime() - now.getTime()) / 86400000;
    if (endDay < now || st === 'expired') {
      return { label: 'Vencido', className: 'bg-red-100 text-red-800' };
    }
    if (diffDays >= 0 && diffDays <= 30) {
      return { label: 'Por vencer', className: 'bg-amber-100 text-amber-900' };
    }
  }
  return { label: 'Activo', className: 'bg-emerald-100 text-emerald-800' };
}

function paymentStatusLabel(s: string) {
  if (s === 'paid') return 'Pagado';
  if (s === 'partial') return 'Parcial';
  return 'Pendiente';
}

function paymentStatusBadgeClass(s: string) {
  if (s === 'paid') return 'bg-emerald-100 text-emerald-800';
  if (s === 'partial') return 'bg-amber-100 text-amber-900';
  return 'bg-gray-100 text-gray-700';
}

function maintenanceStatusUi(s: string) {
  if (s === 'resolved') return { label: 'Resuelto', cls: 'bg-emerald-100 text-emerald-800' };
  if (s === 'reviewed') return { label: 'En proceso', cls: 'bg-blue-100 text-blue-800' };
  if (s === 'in_progress') return { label: 'En curso', cls: 'bg-sky-100 text-sky-800' };
  return { label: 'Pendiente', cls: 'bg-gray-100 text-gray-700' };
}

function tenantQuotationCostLine(r: {
  quotationSubmittedAt?: string | null;
  quotationIsFree?: boolean | null;
  quotationAmount?: string | number | null;
}): string | null {
  if (!r.quotationSubmittedAt) return null;
  if (r.quotationIsFree === true) return 'Cotización: sin costo para ti';
  const v = r.quotationAmount;
  if (v === null || v === undefined || v === '') return 'Cotización: monto por confirmar';
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  if (Number.isNaN(n)) return 'Cotización: monto por confirmar';
  const fmt = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
  return `Cotización estimada: ${fmt}`;
}

function tenantQuotationApprovalBadge(r: {
  quotationSubmittedAt?: string | null;
  quotationApprovedAt?: string | null;
}): { label: string; cls: string } | null {
  if (!r.quotationSubmittedAt) return null;
  if (!r.quotationApprovedAt) {
    return { label: 'Aprobación admin pendiente', cls: 'bg-amber-100 text-amber-900' };
  }
  return { label: 'Cotización aprobada', cls: 'bg-teal-100 text-teal-900' };
}

function parsePriorityFromNotes(notes: string | null | undefined): string {
  if (!notes) return '—';
  const m = notes.match(/Prioridad:\s*(\S+)/i);
  if (!m) return '—';
  const p = m[1].toLowerCase();
  const map: Record<string, string> = {
    baja: 'Baja',
    media: 'Media',
    alta: 'Alta',
    urgente: 'Urgente',
    low: 'Baja',
    medium: 'Media',
    high: 'Alta',
    urgent: 'Urgente',
  };
  return map[p] ?? m[1];
}

function renewalStatusUi(status: string) {
  const u = (status || '').toLowerCase();
  if (u === 'approved' || u === 'aprobada' || u === 'aprobado') {
    return { label: 'Aprobada', cls: 'bg-emerald-100 text-emerald-800' };
  }
  if (u === 'rejected' || u === 'rechazada' || u === 'rechazado') {
    return { label: 'Rechazada', cls: 'bg-red-100 text-red-800' };
  }
  return { label: 'Pendiente', cls: 'bg-amber-100 text-amber-900' };
}

type ModalFeedback = { type: 'success' | 'error'; message: string } | null;

export const TenantPortal: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]['id']>('account');

  const [isRenewalOpen, setIsRenewalOpen] = useState(false);
  const [renewalForm, setRenewalForm] = useState({ message: '', proposedStartDate: '', proposedEndDate: '' });
  const [renewalFeedback, setRenewalFeedback] = useState<ModalFeedback>(null);

  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportForm, setReportForm] = useState({
    title: '',
    findings: '',
    priority: 'media' as 'baja' | 'media' | 'alta' | 'urgente',
  });
  const [reportFeedback, setReportFeedback] = useState<ModalFeedback>(null);
  const [viewCommentsReportId, setViewCommentsReportId] = useState<string | null>(null);
  const reportSuccessCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearReportSuccessTimer = () => {
    if (reportSuccessCloseTimerRef.current !== null) {
      clearTimeout(reportSuccessCloseTimerRef.current);
      reportSuccessCloseTimerRef.current = null;
    }
  };

  useEffect(() => () => clearReportSuccessTimer(), []);

  const { data: tenant, isLoading, error } = useQuery({
    queryKey: ['tenant-me'],
    queryFn: () => api.getMyTenantProfile(),
    staleTime: 30_000,
  });

  const enabledLists = !!tenant && !error;

  const paymentsQuery = useQuery({
    queryKey: ['tenant-me-payments'],
    queryFn: () => api.getMyTenantPayments(),
    enabled: enabledLists,
    staleTime: 30_000,
  });

  const reportsQuery = useQuery({
    queryKey: ['tenant-maintenance-reports'],
    queryFn: () => api.getMyTenantMaintenanceReports(),
    enabled: enabledLists,
    staleTime: 30_000,
  });

  const renewalsQuery = useQuery({
    queryKey: ['tenant-renewal-requests'],
    queryFn: () => api.getMyTenantRenewalRequests(),
    enabled: enabledLists,
    staleTime: 30_000,
  });

  const propertyName = useMemo(() => tenant?.properties?.name ?? '—', [tenant]);

  const contractBadge = useMemo(() => (tenant ? getContractBadge(tenant) : null), [tenant]);

  const renewalMutation = useMutation({
    mutationFn: (payload: {
      message?: string;
      proposedStartDate?: string;
      proposedEndDate?: string;
    }) => api.requestTenantRenewal(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-renewal-requests'] });
      setRenewalFeedback({ type: 'success', message: 'Solicitud enviada correctamente.' });
    },
    onError: (err: any) =>
      setRenewalFeedback({ type: 'error', message: err?.message || 'No se pudo enviar la solicitud.' }),
  });

  const reportMutation = useMutation({
    mutationFn: (payload: {
      title: string;
      findings: string;
      priority?: 'baja' | 'media' | 'alta' | 'urgente';
    }) => api.createTenantMaintenanceReport(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-maintenance-reports'] });
      setReportFeedback({ type: 'success', message: 'Reporte enviado. Gracias.' });
      clearReportSuccessTimer();
      reportSuccessCloseTimerRef.current = setTimeout(() => {
        reportSuccessCloseTimerRef.current = null;
        setIsReportOpen(false);
        setReportFeedback(null);
        setReportForm({ title: '', findings: '', priority: 'media' });
      }, 1500);
    },
    onError: (err: any) =>
      setReportFeedback({ type: 'error', message: err?.message || 'No se pudo enviar el reporte.' }),
  });

  const openRenewalModal = () => {
    setRenewalFeedback(null);
    setRenewalForm({ message: '', proposedStartDate: '', proposedEndDate: '' });
    setIsRenewalOpen(true);
  };

  const closeRenewalModal = () => {
    if (renewalMutation.isPending) return;
    const wasSuccess = renewalFeedback?.type === 'success';
    setIsRenewalOpen(false);
    setRenewalFeedback(null);
    if (wasSuccess) {
      setRenewalForm({ message: '', proposedStartDate: '', proposedEndDate: '' });
    }
  };

  const openReportModal = () => {
    clearReportSuccessTimer();
    setReportFeedback(null);
    setReportForm({ title: '', findings: '', priority: 'media' });
    setIsReportOpen(true);
  };

  const closeReportModal = () => {
    if (reportMutation.isPending) return;
    clearReportSuccessTimer();
    const wasSuccess = reportFeedback?.type === 'success';
    setIsReportOpen(false);
    setReportFeedback(null);
    if (wasSuccess) {
      setReportForm({ title: '', findings: '', priority: 'media' });
      queryClient.invalidateQueries({ queryKey: ['tenant-maintenance-reports'] });
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary';

  const maintenanceList = reportsQuery.data?.data ?? [];
  const viewCommentsReport = useMemo(
    () => maintenanceList.find((r: { id: string }) => r.id === viewCommentsReportId) ?? null,
    [maintenanceList, viewCommentsReportId],
  );

  if (isLoading) {
    return <div className="flex justify-center py-12 text-gray-500">Cargando…</div>;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{(error as Error).message || 'Error al cargar'}</p>
      </div>
    );
  }

  const payments = paymentsQuery.data?.payments ?? [];
  const summary = paymentsQuery.data?.summary;
  const renewalList = renewalsQuery.data?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Portal del inquilino</h1>
        <p className="text-sm text-gray-500 mt-1">Consulta tu contrato, pagos y solicitudes.</p>
      </div>

      <nav
        className="flex gap-1 overflow-x-auto border-b border-gray-200 pb-0 -mx-1 px-1 scrollbar-thin"
        aria-label="Secciones"
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`shrink-0 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary bg-white'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'account' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Mi información">
            <div className="space-y-3 text-sm">
              <div className="flex flex-col sm:flex-row sm:gap-2">
                <span className="text-gray-500 shrink-0 sm:w-40">Nombre completo</span>
                <span className="font-medium text-gray-900">{tenant?.name ?? '—'}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-2">
                <span className="text-gray-500 shrink-0 sm:w-40">Email</span>
                <span className="font-medium text-gray-900 break-all">{tenant?.email ?? '—'}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-2">
                <span className="text-gray-500 shrink-0 sm:w-40">Teléfono</span>
                <span className="font-medium text-gray-900">
                  {tenant?.phone ? formatPhoneDisplay(String(tenant.phone)) : '—'}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-2">
                <span className="text-gray-500 shrink-0 sm:w-40">Propiedad asignada</span>
                <span className="font-medium text-gray-900">{propertyName}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-2">
                <span className="text-gray-500 shrink-0 sm:w-40">Unidad asignada</span>
                <span className="font-medium text-gray-900">{tenant?.unitNumber ?? '—'}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-2">
                <span className="text-gray-500 shrink-0 sm:w-40">Contrato</span>
                <span className="font-medium text-gray-900">
                  {formatDateLong(tenant?.startDate)} – {formatDateLong(tenant?.endDate)}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                <span className="text-gray-500 shrink-0 sm:w-40">Renta mensual</span>
                <span className="font-medium text-gray-900">
                  {formatMoney(Number(tenant?.monthlyRent ?? 0))}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                <span className="text-gray-500 shrink-0 sm:w-40">Estado del contrato</span>
                {contractBadge && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${contractBadge.className}`}>
                    {contractBadge.label}
                  </span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-2">
                <span className="text-gray-500 shrink-0 sm:w-40">Depósito</span>
                <span className="text-gray-600">No registrado en el sistema</span>
              </div>
            </div>
          </Card>

          <Card title="Encargado">
            {tenant?.assignedManager ? (
              <div className="space-y-3 text-sm">
                <div className="flex flex-col sm:flex-row sm:gap-2">
                  <span className="text-gray-500 shrink-0 sm:w-36">Nombre</span>
                  <span className="font-medium text-gray-900">{tenant.assignedManager.name}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:gap-2">
                  <span className="text-gray-500 shrink-0 sm:w-36">Correo</span>
                  <span className="font-medium text-gray-900 break-all">{tenant.assignedManager.email}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:gap-2">
                  <span className="text-gray-500 shrink-0 sm:w-36">Teléfono</span>
                  <span className="font-medium text-gray-900">
                    {(tenant.assignedManager as { phone?: string }).phone
                      ? formatPhoneDisplay(String((tenant.assignedManager as { phone?: string }).phone))
                      : '—'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/80 px-4 py-8 text-center">
                <p className="text-gray-600 text-sm leading-relaxed">
                  Aún no hay un encargado asignado a tu cuenta. El equipo te contactará cuando corresponda.
                </p>
              </div>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="space-y-6">
          <Card title="Resumen">
            {paymentsQuery.isLoading ? (
              <p className="text-sm text-gray-500">Cargando pagos…</p>
            ) : paymentsQuery.isError ? (
              <p className="text-sm text-red-600">No se pudieron cargar los pagos.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3">
                  <p className="text-gray-500">Total pagado (estimado)</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {formatMoney(Number(summary?.totalPaidAmount ?? 0))}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{summary?.totalPaidCount ?? 0} periodo(s) marcado(s) como pagado</p>
                </div>
                <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3">
                  <p className="text-gray-500">Total pendiente (estimado)</p>
                  <p className="text-lg font-semibold text-amber-900 mt-1">
                    {formatMoney(Number(summary?.totalPendingAmount ?? 0))}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{summary?.totalPendingCount ?? 0} periodo(s) con saldo</p>
                </div>
                <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3">
                  <p className="text-gray-500">Próximo pago</p>
                  {summary?.nextPayment ? (
                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {formatMonthYearLong(summary.nextPayment.periodYear, summary.nextPayment.periodMonth)}
                    </p>
                  ) : (
                    <p className="text-lg font-semibold text-emerald-800 mt-1">Al corriente</p>
                  )}
                  {summary?.nextPayment && (
                    <p className="text-xs text-gray-500 mt-1">
                      {formatMoney(Number(summary.nextPayment.amount ?? paymentsQuery.data?.monthlyRent ?? 0))} ·{' '}
                      {paymentStatusLabel(summary.nextPayment.status)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </Card>

          <Card title="Historial de pagos">
            {payments.length === 0 && !paymentsQuery.isLoading ? (
              <p className="text-sm text-gray-500 text-center py-6">No hay pagos registrados</p>
            ) : (
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <table className="min-w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-500 font-medium">
                      <th className="py-2 pr-4">Mes</th>
                      <th className="py-2 pr-4">Monto</th>
                      <th className="py-2 pr-4">Estado</th>
                      <th className="py-2">Fecha de registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((row: any) => (
                      <tr key={row.id} className="border-b border-gray-100">
                        <td className="py-2.5 pr-4 text-gray-900 capitalize">
                          {formatMonthYearLong(row.periodYear, row.periodMonth)}
                        </td>
                        <td className="py-2.5 pr-4 font-medium">{formatMoney(Number(row.amount ?? 0))}</td>
                        <td className="py-2.5 pr-4">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${paymentStatusBadgeClass(
                              row.status,
                            )}`}
                          >
                            {paymentStatusLabel(row.status)}
                          </span>
                        </td>
                        <td className="py-2.5 text-gray-600">{formatDateTime(row.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-gray-600">Reportes de mantenimiento asociados a tu propiedad.</p>
            <Button variant="secondary" className="shrink-0 w-full sm:w-auto" onClick={openReportModal}>
              Levantar reporte
            </Button>
          </div>

          <Card title="Mis reportes de mantenimiento">
            {reportsQuery.isLoading ? (
              <p className="text-sm text-gray-500">Cargando…</p>
            ) : maintenanceList.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No has levantado reportes</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {maintenanceList.map((r: any) => {
                  const st = maintenanceStatusUi(r.status);
                  const qLine = tenantQuotationCostLine(r);
                  const qAppr = tenantQuotationApprovalBadge(r);
                  return (
                    <li key={r.id} className="py-4 first:pt-0">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div>
                          <p className="font-medium text-gray-900">{r.title}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatDateTime(r.createdAt)}</p>
                          {qLine ? <p className="text-xs text-gray-700 mt-1.5 font-medium">{qLine}</p> : null}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${st.cls}`}>
                            {st.label}
                          </span>
                          {qAppr ? (
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${qAppr.cls}`}>
                              {qAppr.label}
                            </span>
                          ) : null}
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            Prioridad: {parsePriorityFromNotes(r.notes)}
                          </span>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="text-xs"
                            onClick={() => setViewCommentsReportId(r.id)}
                          >
                            Ver historial de trabajo
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'renewal' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-gray-600">Solicita la renovación de tu contrato y revisa el estado de tus solicitudes.</p>
            <Button variant="secondary" className="shrink-0 w-full sm:w-auto" onClick={openRenewalModal}>
              Solicitud de renovación
            </Button>
          </div>

          <Card title="Mis solicitudes de renovación">
            {renewalsQuery.isLoading ? (
              <p className="text-sm text-gray-500">Cargando…</p>
            ) : renewalList.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No hay solicitudes registradas</p>
            ) : (
              <ul className="space-y-4">
                {renewalList.map((req: any) => {
                  const st = renewalStatusUi(req.status);
                  return (
                    <li
                      key={req.id}
                      className="rounded-lg border border-gray-100 bg-gray-50/50 px-4 py-3 space-y-2"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs text-gray-500">
                          Solicitud:{' '}
                          {formatDateTime(req.createdAt)}
                        </span>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${st.cls}`}>
                          {st.label}
                        </span>
                      </div>
                      <div className="text-sm text-gray-700">
                        <span className="text-gray-500">Fechas propuestas: </span>
                        {req.proposedStartDate || req.proposedEndDate ? (
                          <>
                            {formatDate(req.proposedStartDate)} – {formatDate(req.proposedEndDate)}
                          </>
                        ) : (
                          'No indicadas'
                        )}
                      </div>
                      {req.message ? (
                        <p className="text-sm text-gray-600 border-t border-gray-200 pt-2 mt-1">
                          <span className="text-gray-500">Mensaje: </span>
                          {req.message}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>
      )}

      <Modal isOpen={isRenewalOpen} onClose={closeRenewalModal} title="Solicitud de renovación" size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Completa el formulario. Tu encargado o el equipo operativo recibirán una notificación por correo.
          </p>
          {renewalFeedback && (
            <div
              className={`rounded-lg px-3 py-2 text-sm ${
                renewalFeedback.type === 'success'
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-100'
                  : 'bg-red-50 text-red-800 border border-red-100'
              }`}
            >
              {renewalFeedback.message}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">¿Por qué desea renovar? (opcional)</label>
            <textarea
              value={renewalForm.message}
              onChange={(e) => setRenewalForm((p) => ({ ...p, message: e.target.value }))}
              rows={3}
              className={inputClass}
              placeholder="Ej. Deseo continuar 12 meses más…"
              disabled={renewalMutation.isPending}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha propuesta de inicio</label>
              <input
                type="date"
                value={renewalForm.proposedStartDate}
                onChange={(e) => setRenewalForm((p) => ({ ...p, proposedStartDate: e.target.value }))}
                className={inputClass}
                disabled={renewalMutation.isPending}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha propuesta de fin</label>
              <input
                type="date"
                value={renewalForm.proposedEndDate}
                onChange={(e) => setRenewalForm((p) => ({ ...p, proposedEndDate: e.target.value }))}
                className={inputClass}
                disabled={renewalMutation.isPending}
              />
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={closeRenewalModal}
              disabled={renewalMutation.isPending}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cerrar
            </button>
            <button
              type="button"
              onClick={() => {
                setRenewalFeedback(null);
                renewalMutation.mutate({
                  message: renewalForm.message.trim() || undefined,
                  proposedStartDate: renewalForm.proposedStartDate || undefined,
                  proposedEndDate: renewalForm.proposedEndDate || undefined,
                });
              }}
              disabled={renewalMutation.isPending}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {renewalMutation.isPending ? 'Enviando…' : 'Enviar solicitud'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isReportOpen} onClose={closeReportModal} title="Levantar reporte" size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Describe el problema. El equipo de mantenimiento recibirá una notificación.
          </p>
          {reportFeedback && (
            <div
              className={`rounded-lg px-3 py-2 text-sm ${
                reportFeedback.type === 'success'
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-100'
                  : 'bg-red-50 text-red-800 border border-red-100'
              }`}
            >
              {reportFeedback.message}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
            <input
              value={reportForm.title}
              onChange={(e) => setReportForm((p) => ({ ...p, title: e.target.value }))}
              className={inputClass}
              placeholder="Ej. Fuga en lavabo"
              disabled={reportMutation.isPending}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              value={reportForm.findings}
              onChange={(e) => setReportForm((p) => ({ ...p, findings: e.target.value }))}
              rows={5}
              className={inputClass}
              placeholder="Describe qué ocurre, desde cuándo…"
              disabled={reportMutation.isPending}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
            <select
              value={reportForm.priority}
              onChange={(e) =>
                setReportForm((p) => ({
                  ...p,
                  priority: e.target.value as 'baja' | 'media' | 'alta' | 'urgente',
                }))
              }
              className={inputClass}
              disabled={reportMutation.isPending}
            >
              <option value="baja">Baja</option>
              <option value="media">Media</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={closeReportModal}
              disabled={reportMutation.isPending}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cerrar
            </button>
            <button
              type="button"
              onClick={() => {
                setReportFeedback(null);
                const title = reportForm.title.trim();
                const findings = reportForm.findings.trim();
                if (!title || !findings) {
                  setReportFeedback({ type: 'error', message: 'Completa título y descripción.' });
                  return;
                }
                reportMutation.mutate({ title, findings, priority: reportForm.priority });
              }}
              disabled={reportMutation.isPending}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {reportMutation.isPending ? 'Enviando…' : 'Enviar reporte'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!viewCommentsReportId && !!viewCommentsReport}
        onClose={() => setViewCommentsReportId(null)}
        title={viewCommentsReport?.title ?? 'Historial de trabajo'}
        size="lg"
      >
        {viewCommentsReport && (
          <div className="space-y-2">
            <p className="text-sm text-gray-600">{formatDateTime(viewCommentsReport.createdAt)}</p>
            <MaintenanceReportWorkTimeline
              reportId={viewCommentsReport.id}
              status={viewCommentsReport.status}
              readOnly
              quotation={{
                isFree: viewCommentsReport.quotationIsFree,
                amount: viewCommentsReport.quotationAmount,
                notes: viewCommentsReport.quotationNotes,
                submittedAt: viewCommentsReport.quotationSubmittedAt,
                approvedAt: viewCommentsReport.quotationApprovedAt,
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};
